import React, { useState, useEffect, useRef } from "react";
import { X, Loader2 } from "lucide-react";
import { getSuggestKeywords } from "Explore/Services/Common";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";

const SubjectAreaFilter = ({ filters, setFilters, entity = "subjectArea" }) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const selectedTags = filters[entity] || [];

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (val) => {
    setQuery(val);
    if (!val.trim()) {
      setOptions([]);
      setIsOpen(false);
      return;
    }
    
    setIsOpen(true);
    setIsLoading(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      getSuggestKeywords(val, (res) => {
        setOptions(res || []);
        setIsLoading(false);
      });
    }, 300);
  };

  const handleSelect = (option) => {
    const val = option.name || option.value;
    if (!selectedTags.includes(val)) {
      setFilters({ [entity]: [...selectedTags, val] });
    }
    setQuery("");
    setIsOpen(false);
    setOptions([]);
  };

  const handleRemove = (tagToRemove) => {
    setFilters({ [entity]: selectedTags.filter((t) => t !== tagToRemove) });
  };

  return (
    <div className="flex flex-col gap-2 w-full" ref={containerRef}>
      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedTags.map((tag) => (
            <Badge key={tag} variant="secondary" className="px-2 py-1 flex items-center gap-1 font-normal bg-explore/10 text-explore hover:bg-explore/20">
              {tag}
              <X
                size={12}
                className="cursor-pointer text-gray-500 hover:text-gray-900"
                onClick={() => handleRemove(tag)}
              />
            </Badge>
          ))}
        </div>
      )}

      {/* Input & Dropdown */}
      <div className="relative">
        <Input
          placeholder="Search subject areas..."
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => {
            if (query.length > 0) setIsOpen(true);
          }}
          className="w-full bg-white border-gray-300 h-9"
        />

        {isOpen && query.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-48 overflow-y-auto py-1">
            {isLoading && (
              <div className="p-3 flex items-center justify-center">
                <Loader2 className="text-gray-400 animate-spin" size={16} />
              </div>
            )}
            
            {!isLoading && options.length === 0 && (
              <div className="p-3 text-center text-sm text-gray-500">No results found.</div>
            )}
            
            {!isLoading && options.map((opt, idx) => (
              <div
                key={opt.id || idx}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleSelect(opt)}
              >
                {opt.name || opt.value}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectAreaFilter;
